import { chatKey, updateChatList } from './chatList';

/** Identidade (`chatKey`) da conversa aberta, montada a partir do estado. */
const selectedKey = (state: any): string | null =>
  chatKey({
    canal_id: state.selectedChatCanalId,
    canal_cliente_id: state.selectedChatClientId,
  } as any);


export const chatDispatchAction = {
  CONNECT: 'CONNECT',
  DISCONNECT: 'DISCONNECT',
  UPDATE_CHATS: 'UPDATE_CHATS',
  SET_SELECTED_CHAT_ID: 'SET_SELECTED_CHAT_ID',
  SET_CHATS: 'SET_CHATS',
  SET_CHATS_LIST_CURSOR: 'SET_CHATS_LIST_CURSOR',
  UPSERT_CHAT: 'UPSERT_CHAT',
  INSERT_CHAT: 'INSERT_CHAT',
  REMOVE_CHAT: 'REMOVE_CHAT',
  UPDATE_CHAT: 'UPDATE_CHAT',
  SET_CHATS_CURSOR: 'SET_CHATS_CURSOR',
  SET_HAS_MORE_CHATS: 'SET_HAS_MORE_CHATS',
  SET_HAS_MORE_PAGES: 'SET_HAS_MORE_PAGES',
  SET_LOADING_CHATS: 'SET_LOADING_CHATS',
  UPDATE_FILA: 'UPDATE_FILA',
  SET_FILA: 'SET_FILA',
  ADD_FILA: 'ADD_FILA',
  REMOVE_FILA: 'REMOVE_FILA',
  UPDATE_FILA_CALL: 'UPDATE_FILA_CALL',
  SET_FILA_CALL: 'SET_FILA_CALL',
  ADD_FILA_CALL: 'ADD_FILA_CALL',
  REMOVE_FILA_CALL: 'REMOVE_FILA_CALL',
  SET_SENDING_MESSAGE: 'SET_SENDING_MESSAGE',
  SET_CHAT_MESSAGES: 'SET_CHAT_MESSAGES',
  ADD_CHAT_MESSAGE: 'ADD_CHAT_MESSAGE',
  ADD_CHAT_MESSAGES: 'ADD_CHAT_MESSAGES',
  UPDATE_CHAT_MESSAGE: 'UPDATE_CHAT_MESSAGE',
  REMOVE_CHAT_MESSAGE: 'REMOVE_CHAT_MESSAGE',
  SET_SERVER_ERROR: 'SET_SERVER_ERROR',
  LOADING_MESSAGES: 'LOADING_MESSAGES',
  SET_PARAMS: 'SET_PARAMS',
  SET_AUDIO_DEVICES_WARNING: 'SET_AUDIO_DEVICES_WARNING',
  SET_CAN_HANDLE_CALLS: 'SET_CAN_HANDLE_CALLS',
};

export type ActionType = keyof typeof chatDispatchAction;

export type Action = {
  type: ActionType;
  payload?: any;
};

export const chatReducer = (state: any, action: Action) => {
  console.log('reducer called', action, state);

  switch (action.type) {
    case chatDispatchAction.CONNECT:
      return { ...state, isConnected: true };
    case chatDispatchAction.DISCONNECT:
      return { ...state, isConnected: false };
    case chatDispatchAction.SET_SELECTED_CHAT_ID:
      return {
        ...state,
        selectedChatId: action.payload?.chat_id,
        selectedChatCanalId: action.payload?.canal_id,
        selectedChatClientId: action.payload?.canal_cliente_id,
        selectedChatSnapshot: null,
      };
    case chatDispatchAction.SET_CHATS:
      return { ...state, chats: action.payload };
    case chatDispatchAction.SET_CHATS_LIST_CURSOR: {
      // Paginação: acrescenta a página recebida, eliminando duplicatas.
      // A keyset-pagination do servidor pode ter sobreposição (ex.: o último
      // item da página 1 como primeiro da página 2), e eventos (UPSERT_CHAT,
      // mensagens novas) podem ter inserido um item que depois vem na próxima
      // página. De-duplicar pela chave do chat.
      if (action.payload.prevCursor) {
        const existing = new Set(
          state.chats.map((chat: any) => chatKey(chat))
        );
        const newItems = action.payload.items.filter(
          (item: any) => !existing.has(chatKey(item))
        );
        return { ...state, chats: [...state.chats, ...newItems] };
      }

      // Recarga completa: a lista volta para a primeira página. Se a conversa
      // aberta estava numa página seguinte, ela sai de `chats` — guardamos uma
      // cópia para o painel continuar aberto (ver `selectedChatSnapshot`).
      // Só o botão "atualizar" pede isso; troca de filtro/busca segue fechando
      // a conversa que deixou de casar com o filtro.
      const list = action.payload.items;
      const key = selectedKey(state);
      // A cópia anterior entra como fallback: no segundo "atualizar" seguido a
      // conversa aberta já não está em `state.chats` e seria perdida aqui.
      const aberta =
        action.payload.preservarSelecao && key
          ? state.chats?.find((chat: any) => chatKey(chat) === key) ??
            state.selectedChatSnapshot
          : null;

      return {
        ...state,
        chats: list,
        selectedChatSnapshot:
          aberta && !list.some((chat: any) => chatKey(chat) === key)
            ? aberta
            : null,
      };
    }
    case chatDispatchAction.UPSERT_CHAT: {
      if (!action.payload.chat?.canal_cliente_id) {
        console.warn('Cannot upsert chat without canal_cliente_id', action.payload);
        return state;
      }

      if (!action.payload.chat?.canal_id) {
        console.warn('Cannot upsert chat without canal_id', action.payload);
        return state;
      }

      const chatIdx = state.chats?.findIndex(
        (chat: any) =>
          chat.canal_cliente_id == action.payload.chat.canal_cliente_id && chat.canal_id == action.payload.chat.canal_id,
      );
      if (chatIdx > -1) {
        return {
          ...state,
          chats: updateChatList(
            state.chats,
            chatIdx,
            (chat: any) => ({
              ...chat,
              ...action.payload.chat,
            }),
            action.payload.move,
          ),
        };
      }

      // Conversa preservada recebeu atualização: volta à lista na posição correta.
      // Limpa a preservação pois agora a lista contém dados vivos do servidor.
      if (state.selectedChatSnapshot && chatKey(action.payload.chat) === selectedKey(state)) {
        return {
          ...state,
          chats: updateChatList(
            [{ ...state.selectedChatSnapshot, ...action.payload.chat }, ...(state.chats ?? [])],
            0,
            (chat: any) => chat,
            action.payload.move,
          ),
          selectedChatSnapshot: null,
        };
      }

      return {
        ...state,
        chats: updateChatList(
          [action.payload.chat, ...(state.chats ?? [])],
          0,
          (chat: any) => chat,
          action.payload.move,
        ),
      };
    }

    case chatDispatchAction.INSERT_CHAT: {
      return {
        ...state,
        chats: updateChatList(
          [action.payload.chat, ...(state.chats ?? [])],
          0,
          (chat: any) => chat,
          action.payload.move,
        ),
      };
    }
    case chatDispatchAction.UPDATE_CHAT: {
      const chatIdx = state.chats?.findIndex(
        (chat: any) =>
          chat.canal_cliente_id == action.payload.chat.canal_cliente_id && chat.canal_id == action.payload.chat.canal_id,
      );
      if (chatIdx > -1) {
        return {
          ...state,
          chats: updateChatList(
            state.chats,
            chatIdx,
            (chat: any) => ({
              ...chat,
              ...action.payload.chat,
            }),
            action.payload.move,
          ),
        };
      }

      // Fora da lista: se é a conversa preservada, mescla a atualização
      // (UPDATE é raro e pode ser cosmético, como marcador).
      if (
        state.selectedChatSnapshot &&
        chatKey(action.payload.chat) === selectedKey(state)
      ) {
        return {
          ...state,
          selectedChatSnapshot: {
            ...state.selectedChatSnapshot,
            ...action.payload.chat,
          },
        };
      }
      return state;
    }

    case chatDispatchAction.REMOVE_CHAT:
      return {
        ...state,
        chats: state.chats.filter((chat: any) => chat.id != action.payload.id),
        selectedChatId:
          state.selectedChatId == action.payload.id
            ? null
            : state.selectedChatId,
        selectedChatCanalId:
          state.selectedChatCanalId == action.payload.canal_id
            ? null
            : state.selectedChatCanalId,
        selectedChatClientId:
          state.selectedChatClientId == action.payload.canal_cliente_id
            ? null
            : state.selectedChatClientId,
        selectedChatMessages:
          (state.selectedChatId == action.payload.id)
            ? null
            : state.selectedChatMessages,
        selectedChatSnapshot:
          (state.selectedChatId == action.payload.id)
            ? null
            : state.selectedChatSnapshot,
      };
    case chatDispatchAction.SET_CHATS_CURSOR:
      return { ...state, chatsCursor: action.payload };
    case chatDispatchAction.SET_HAS_MORE_CHATS:
      return { ...state, hasMoreChats: action.payload };
    case chatDispatchAction.SET_LOADING_CHATS:
      return { ...state, loadingChats: action.payload };
    case chatDispatchAction.UPDATE_CHATS:
      return { ...state, chats: action.payload };
    case chatDispatchAction.ADD_FILA:
      return {
        ...state,
        fila: [...state.fila, { id: action.payload.chat_id, fila_data_criacao: action.payload.fila_data_criacao }],
      };
    case chatDispatchAction.REMOVE_FILA:
      return {
        ...state,
        fila: state.fila.filter(
          (chat: any) => chat.id != action.payload.chat_id,
        ),
      };
    case chatDispatchAction.SET_FILA:
      return { ...state, fila: action.payload };
    case chatDispatchAction.ADD_FILA_CALL:
      return {
        ...state,
        filaCall: [
          ...state.filaCall,
          {
            id: action.payload.id,
            fila_data_criacao: action.payload.fila_data_criacao,
            sdp_offer: action.payload.sdp_offer,
            ice_candidates: action.payload.ice_candidates,
          }
        ],
      };
    case chatDispatchAction.REMOVE_FILA_CALL:
      return {
        ...state,
        filaCall: state.filaCall.filter(
          (chamada: any) => chamada.id != action.payload.id,
        ),
      };
    case chatDispatchAction.SET_FILA_CALL:
      return { ...state, filaCall: action.payload };
    case chatDispatchAction.UPDATE_FILA_CALL: {
      return {
        ...state,
        filaCall: state.filaCall.map((chamada: any) => {
          if (chamada.id === action.payload.id) {
            return {
              ...chamada,
              ...action.payload.update,
            };
          }
          return chamada;
        }),
      };
    }
    case chatDispatchAction.SET_HAS_MORE_PAGES:
      return { ...state, hasMorePages: action.payload };
    case chatDispatchAction.SET_SENDING_MESSAGE:
      return { ...state, sendingMessage: action.payload };
    case chatDispatchAction.SET_CHAT_MESSAGES:
      return { ...state, selectedChatMessages: action.payload };
    case chatDispatchAction.ADD_CHAT_MESSAGE:
      if (!state.selectedChatClientId) return state;

      if (
        state.selectedChatClientId != null &&
        action.payload?.chat?.canal_cliente_id != null &&
        action.payload.chat.canal_cliente_id != state.selectedChatClientId
      )
        return state;

      if (
        state.selectedChatCanalId != null &&
        action.payload?.chat?.canal_id != null &&
        action.payload.chat.canal_id != state.selectedChatCanalId
      )
        return state;

      // Verificar se a mensagem já existe para evitar duplicações
      if (state.selectedChatMessages?.some((msg: any) => msg.id === action.payload.id)) {
        return state;
      }

      return {
        ...state,
        selectedChatMessages: [
          action.payload,
          ...(state.selectedChatMessages ?? []),
        ],
      };
    case chatDispatchAction.ADD_CHAT_MESSAGES:
      if (
        !action.payload?.length ||
        action.payload[0].canal_cliente_id != state.selectedChatClientId ||
        action.payload[0].canal_id != state.selectedChatCanalId
      )
        return state;
      return {
        ...state,
        selectedChatMessages: [
          ...(state.selectedChatMessages ?? []),
          ...action.payload,
        ],
      };
    case chatDispatchAction.UPDATE_CHAT_MESSAGE: {
      if (!state.selectedChatMessages) return state;

      if (
        state.selectedChatCanalId == null ||
        state.selectedChatCanalId != action.payload.canal_id
      )
        return state;

      if (!action.payload.mensagem) return state;

      const msgIdx = state.selectedChatMessages?.findIndex(
        (msg: any) => msg.id == action.payload.mensagem.id,
      );
      if (msgIdx === -1) return state;

      const newList = [
        ...state.selectedChatMessages.slice(0, msgIdx),
        {
          ...state.selectedChatMessages[msgIdx],
          ...action.payload.mensagem,
        },
        ...state.selectedChatMessages.slice(msgIdx + 1),
      ];

      return { ...state, selectedChatMessages: newList };
    }
    // Mensagem apagada na origem (ex.: o dono do número apagou pelo app
    // WhatsApp Business). O servidor não a devolve mais, então some da lista.
    case chatDispatchAction.REMOVE_CHAT_MESSAGE: {
      if (!state.selectedChatMessages) return state;

      if (
        state.selectedChatCanalId == null ||
        state.selectedChatCanalId != action.payload.canal_id
      )
        return state;

      if (!action.payload.mensagem) return state;

      const restantes = state.selectedChatMessages.filter(
        (msg: any) => msg.id != action.payload.mensagem.id,
      );
      if (restantes.length === state.selectedChatMessages.length) return state;

      return { ...state, selectedChatMessages: restantes };
    }
    case chatDispatchAction.SET_SERVER_ERROR:
      return { ...state, serverError: action.payload };
    case chatDispatchAction.LOADING_MESSAGES:
      return { ...state, loadingMessages: action.payload };
    case chatDispatchAction.SET_PARAMS:
      return { ...state, params: action.payload };
    case chatDispatchAction.SET_AUDIO_DEVICES_WARNING:
      return { ...state, audioDevicesWarning: action.payload };
    case chatDispatchAction.SET_CAN_HANDLE_CALLS:
      return { ...state, canHandleCalls: action.payload };
    default:
      console.warn('action not handled', action);
  }
  return state;
};
